import React, { useState } from 'react';
import { Query } from 'react-apollo';
import { useMutation, useSubscription } from '@apollo/react-hooks';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from 'components/common/Button';
import Spin from 'components/common/Spin';
import {
  GET_STATEMENTS,
  DELETE_STATEMENT,
  UPDATE_STATEMENT,
  LISTEN_ESSAY_CREDITS,
} from 'graphql/essays';
import useSnack from 'hooks/useSnack';
import withConfirm from 'hooks/withConfirm';
import AddStatementModal from './AddStatementModal';
import StatementCard from './StatementCard';
import { DELETE_DOCUMENT } from 'graphql/student/essays';
import ShowLogs from 'modules/student/essays/show-logs';
import DueDateModal from './DueDateModal';
import { useSelector } from 'react-redux';

const PersonalStatement = ({ student, confirm, readOnly }) => {
  const [showModal, toggleModal] = useState(false);
  const [showDateModal, setDateModal] = useState(false);
  const [selected, setSelected] = useState();
  const [showLogs, setShowLogs] = useState();
  const [deleteStatement] = useMutation(DELETE_STATEMENT);
  const [updateStatement] = useMutation(UPDATE_STATEMENT);
  const [deleteDocument] = useMutation(DELETE_DOCUMENT);
  const { data: creditsResp } = useSubscription(LISTEN_ESSAY_CREDITS, {
    variables: { userId: student.id },
  });
  const user = useSelector((s) => s.user.user);

  const snack = useSnack();
  const { product_type } = student;

  const onClose = () => {
    toggleModal(false);
    setSelected();
  };

  const onEdit = (row) => {
    setSelected(row);
    toggleModal(true);
  };

  const onViewLogs = (row) => {
    setShowLogs(row);
  };

  const onDelete = (id) => {
    return deleteStatement({
      variables: { id },
      update: (proxy) => {
        const data = proxy.readQuery({
          query: GET_STATEMENTS,
          variables: {
            userId: student.id,
          },
        });
        proxy.writeQuery({
          query: GET_STATEMENTS,
          variables: {
            userId: student.id,
          },
          data: {
            rows: data.rows.filter((i) => i.id !== id),
          },
        });
      },
    }).then(
      () => snack.success('Statement deleted'),
      (e) => snack.error(e.message),
    );
  };

  const onUpdate = (id, data) => {
    if (data.status === 'draft' && user.type !== 'student') {
      setSelected({
        status: data.status,
        draft_due_date: data.draft_due_date,
        id,
      });
      return setDateModal(true);
    }

    return updateStatement({
      variables: {
        id,
        data,
      },
    });
  };

  const onDeleteDoc = (doc, statement) => {
    confirm(() => {
      return deleteDocument({
        variables: { id: doc.id },
        update: (proxy) => {
          const data = proxy.readQuery({
            query: GET_STATEMENTS,
            variables: {
              userId: student.id,
            },
          });

          statement.documents = statement.documents.filter(
            (d) => d.document.id !== doc.id,
          );
          proxy.writeQuery({
            query: GET_STATEMENTS,
            variables: {
              userId: student.id,
            },
            data: {
              rows: data.rows.map((i) =>
                i.id === statement.id ? statement : i,
              ),
            },
          });
        },
      });
    });
  };

  const cc_essay_total =
    creditsResp?.credits.find((i) => i.credit_type === 'cc_essay')?.available ||
    0;
  const cc_essay_done = Math.abs(
    creditsResp?.credits.find((i) => i.credit_type === 'cc_essay')?.debit || 0,
  );
  const hc_essay_total =
    creditsResp?.credits.find((i) => i.credit_type === 'hc_essay')?.available ||
    0;
  const hc_essay_done = Math.abs(
    creditsResp?.credits.find((i) => i.credit_type === 'hc_essay')?.debit || 0,
  );
  const expert_total =
    creditsResp?.credits.find((i) => i.credit_type === 'expert')?.available ||
    0;
  const expert_done = Math.abs(
    creditsResp?.credits.find((i) => i.credit_type === 'expert')?.debit || 0,
  );

  return (
    <div>
      <Query query={GET_STATEMENTS} variables={{ userId: student.id }}>
        {({ data, loading }) => {
          if (loading) return <Spin spinning linear />;
          const { rows } = data;

          if (!rows.length) {
            return (
              <div>
                <Box textAlign="center" padding={4}>
                  <Typography variant="h5" style={{ marginBottom: 24 }}>
                    Personal Statement
                  </Typography>
                  <Button
                    disabled={readOnly}
                    color="primary"
                    variant="contained"
                    onClick={() => toggleModal(true)}
                  >
                    Add Personal Statement
                  </Button>
                </Box>
              </div>
            );
          } else {
            return (
              <Box>
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column' }}
                  alignItems="center"
                  marginBottom={2}
                >
                  {student.product_type !== 'pro' &&
                    student.product_type !== 'solo' &&
                    student.product_type !== 'cohort' && (
                      <Box>
                        <Typography
                          variant="smallBold"
                          color="textSecondary"
                          style={{ marginRight: 16 }}
                        >
                          CC Revisions Remaining:{' '}
                          {cc_essay_total - cc_essay_done}
                        </Typography>
                        <Typography variant="smallBold" color="textSecondary">
                          HC Revisions Remaining:{' '}
                          {hc_essay_total - hc_essay_done}
                        </Typography>
                      </Box>
                    )}
                  {student.product_type === 'solo' && (
                    <Box marginBottom={1}>
                      <Typography
                        variant="smallBold"
                        color="textSecondary"
                        style={{ marginRight: 16 }}
                      >
                        Expert Credits Remaining:{' '}
                        {expert_total - expert_done}
                      </Typography>
                    </Box>
                  )}
                  {student.product_type === 'cohort' && (
                    <Box marginBottom={1}>
                      <Typography
                        variant="smallBold"
                        color="textSecondary"
                        style={{ marginRight: 16 }}
                      >
                        Expert Credits Remaining:{' '}
                        {expert_total - expert_done}
                      </Typography>
                    </Box>
                  )}
                  <Box marginLeft="auto">
                    <Button
                      disabled={readOnly}
                      color="primary"
                      variant="contained"
                      onClick={() => toggleModal(true)}
                    >
                      Add Personal Statement
                    </Button>
                  </Box>
                </Box>
                <Grid container spacing={3}>
                  {rows.map((i) => (
                    <Grid item sm={12} md={6} lg={6} xl={4} key={i.id}>
                      <StatementCard
                        data={i}
                        readOnly={readOnly}
                        student={student}
                        onDelete={onDelete}
                        onUpdate={onUpdate}
                        onViewLogs={onViewLogs}
                        onEdit={onEdit}
                        onDeleteDoc={(doc) => onDeleteDoc(doc, i)}
                        hasCCCredit={
                          product_type === 'pro'
                            ? true
                            : cc_essay_total - cc_essay_done > 0
                        }
                        hasHCCredit={
                          product_type === 'pro'
                            ? true
                            : hc_essay_total - hc_essay_done > 0
                        }
                        hasExpertCredit={expert_total - expert_done > 0}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          }
        }}
      </Query>
      {showModal && (
        <AddStatementModal
          student={student}
          statement={selected}
          onClose={onClose}
        />
      )}
      {showLogs && (
        <ShowLogs
          context="common-app"
          user={student}
          data={showLogs}
          onClose={() => setShowLogs(false)}
        />
      )}
      {showDateModal && (
        <DueDateModal
          student={student}
          statement={selected}
          onClose={() => {
            setDateModal(false);
          }}
        />
      )}
    </div>
  );
};

export default withConfirm(PersonalStatement);
